import { Box, Button, Flex, Text } from '@mantine/core';
import { IconChevronLeft, IconPhone } from '@tabler/icons-react';
import Loader from 'components/Loader';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CALL_ORDER_PAGE_PATH } from 'routes/paths';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import { useApi } from 'api/api-context';
import panic from 'panic';

/**
 * Page for setting or updating the user's secondary GSM phone number.
 * PhoneInput handles the country flag, dial code, and E.164 formatting.
 */
const SetSecondPhone: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** E.164 value managed by PhoneInput (e.g. "+421912345678"). */
  const [phone, setPhone] = useState('');
  const [hasExisting, setHasExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Load the current secondPhoneNumber from the call-order endpoint and pre-fill the form. */
  useEffect(() => {
    const getCallOrder = getAction('GetCallOrder');
    getCallOrder({})
      .then((response) => {
        setPhone(response.secondPhoneNumber ?? '');
        setHasExisting(Boolean(response.secondPhoneNumber));
        setLoading(false);
      })
      .catch(panic);
  }, [getAction]);

  /** Save the E.164 phone number via the API and navigate back. */
  const handleSave = useCallback(() => {
    if (phone.replace(/\D/g, '').length < 7) {
      setError(_t('Please enter a valid phone number'));
      return;
    }
    setSaving(true);
    const updateSecondPhoneNumber = getAction('UpdateSecondPhoneNumber');
    updateSecondPhoneNumber({ payload: { secondPhoneNumber: phone } })
      .then(() => navigate(CALL_ORDER_PAGE_PATH.original))
      .catch((err: Error) => {
        panic(err);
        setSaving(false);
      });
  }, [getAction, phone, navigate, _t]);

  /** Remove the secondary phone number by sending null to the API. */
  const handleRemove = useCallback(() => {
    setSaving(true);
    const updateSecondPhoneNumber = getAction('UpdateSecondPhoneNumber');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateSecondPhoneNumber({ payload: { secondPhoneNumber: null as any } })
      .then(() => navigate(CALL_ORDER_PAGE_PATH.original))
      .catch((err: Error) => {
        panic(err);
        setSaving(false);
      });
  }, [getAction, navigate]);

  return (
    <SimpleLayout>
      <Box p="1rem">
        {/* Header */}
        <Box mb="1.5rem">
          <Flex>
            <Link
              to={CALL_ORDER_PAGE_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium c={theme.colors?.gray?.[9]}>{_t('Back to call order')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem" align="center">
            <IconPhone size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold c={theme.colors?.gray?.[12]}>{_t('Secondary phone number')}</H1SemiBold>
          </Flex>
          <Text size="sm" c="dimmed" mt="0.25rem">
            {_t('This number will be used as a secondary contact method when someone rings your intercom.')}
          </Text>
        </Box>

        {loading ? (
          <Loader />
        ) : (
          <>
            <Box mb={error ? '0.25rem' : '1rem'}>
              <PhoneInput
                defaultCountry="sk"
                flags={[{ iso2: 'sk', src: '/img/sk.svg' }]}
                value={phone}
                onChange={(val) => {
                  setPhone(val);
                  if (error) setError(null);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: '2.625rem',
                  border: '1px solid var(--mantine-color-default-border)',
                  borderRadius: 'var(--mantine-radius-sm)',
                  backgroundColor: 'var(--mantine-color-default)',
                  fontSize: '16px',
                }}
                inputStyle={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: 'var(--mantine-color-default-color)',
                  fontFamily: 'inherit',
                  fontSize: '16px',
                  padding: '0 0.75rem',
                  width: '100%',
                  borderRadius: '0 var(--mantine-radius-sm) var(--mantine-radius-sm) 0',
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    border: 'none',
                    borderRight: '1px solid var(--mantine-color-default-border)',
                    backgroundColor: 'transparent',
                    height: '100%',
                    padding: '0 8px',
                    cursor: 'pointer',
                    borderRadius: 'var(--mantine-radius-sm) 0 0 var(--mantine-radius-sm)',
                  },
                }}
              />
              {error && (
                <Text size="xs" c="red" mt="0.25rem">
                  {error}
                </Text>
              )}
            </Box>

            <Button fullWidth size="md" onClick={handleSave} loading={saving} mb="0.5rem">
              {_t('Save')}
            </Button>

            {hasExisting && (
              <Button fullWidth size="md" variant="outline" color="red" onClick={handleRemove} loading={saving}>
                {_t('Remove phone number')}
              </Button>
            )}
          </>
        )}
      </Box>
    </SimpleLayout>
  );
};

export default SetSecondPhone;
